import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiService, ServiceType } from "./apiServices";
import { getAuth0Token } from '../auth/utils';
import { traveller_ENDPOINTS } from './apiEndPoints';
import { useLocalization } from '../shared/contexts/LocalizationContext';



// Types
interface ItineraryOrder {
  order_id: string;
  orderCode: string;
  name: string;
  profileImageUrl: string;
  rating: number;
  reviews: number;
  trips: number;
  departureAirport: string;
  destinationAirport: string;
  departureAirportCode?: string;
  destinationAirportCode?: string;
  invNumber: string;
  status: string;
  allocatedWeight: number;
  allocatedPrice: number;
  platform_fee?: number;
  sender_id?: string;
  chat_room_id?: string | null;
  unread_messages_count?: number;
  approved_at?: string;
  checked_in_by_sender?: boolean;
  checked_in_by_traveller?: boolean;
  checked_out_by_traveller?: boolean;
  is_checked_out_by_receiver?: boolean;
  created_at?: string;
}

interface OrderDetails {
  name: string;
  email: string;
  contact: string;
  profileImage: string;
  rating: number;
  sender_id: string;
  shipment_name: string;
  receiver_name: string;
  receiver_email: string;
  receiver_contact: string;
  from: string;
  to: string;
  orderCode: string;
  inv_number: string;
  allocated_weight: number;
  allocated_price: number;
  platform_fee: number;
  status: string;
  checked_in_by_sender: boolean;
  checked_in_by_traveller: boolean;
  checked_out_by_traveller: boolean;
  is_checked_out_by_receiver: boolean;
  created_at?: string;
  drop_of_date?: string;
  drop_of_time?: string;
  departure_date?: string;
  departure_time?: string;
  pick_up_date?: string;
  pick_up_time?: string;
  arrival_date?: string;
  arrival_time?: string;
  items: Array<{
    id: string;
    category: string;
    price: number;
    pieces: number;
    pages: number;
    weight: number;
    images: string[];
    edit_price: number;
    edit_pieces: number;
    edit_pages: number;
    edit_weight: number;
    order_id: string;
    status: string | null;
  }>;

}

interface MonthlyData {
  month: string;
  balance: string;
}

interface WalletMonthlyBalanceResponse {
  year: number;
  current_balance: string;
  currency: string;
  monthly_data: MonthlyData[];
}

interface OrderHistoryItem {
  order_id: string;
  orderCode: string;
  name: string;
  profileImageUrl: string;
  rating: number;
  reviews: number;
  trips: number;
  departureAirport: string;
  destinationAirport: string;
  invNumber: string;
  status: string;
  allocatedWeight: number;
  allocatedPrice: number;
  created_at?: string;
  departure_date?: string;
  arrival_date?: string;
}

interface OrderHistoryResponse {
  orders: OrderHistoryItem[];
  total: number;
}

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
}

export interface CurrencyResponse {
  currencies: Currency[];
}

export interface TravelPreferences {
  user_id: string;
  currency_id: string;
  code: string;          // Currency code (e.g., "USD", "EUR", "GBP")
  name: string;          // Full currency name (e.g., "US Dollar")
  symbol: string;        // Currency symbol (e.g., "$", "€", "£")
  sender_dropoff_time: number;
  receiver_pickup_time: number;
  distance_unit: 'kilometer' | 'mile';
  distance_radius: number;
  multiple_senders: boolean;
  unit_metrics_for_distance: boolean;
}

export interface TravelPreferencesResponse {
  preferences: TravelPreferences;
}

export interface UpdateTravelPreferencesRequest {
  user_id: string;
  currency_id: string;
  sender_dropoff_time: number;
  receiver_pickup_time: number;
  distance_unit: 'kilometer' | 'mile';
  distance_radius: number;
  multiple_senders: boolean;
  unit_metrics_for_distance: boolean;
}

// Query keys
export const travellerKeys = {
  all: ['traveller'] as const,
  // Collections - use these for invalidating all items of a type
  allItineraries: ['traveller', 'itineraries'] as const,
  allMyOrders: ['traveller', 'myOrders'] as const,
  allMyOrderDetails: ['traveller', 'myOrderDetails'] as const,
  allItineraryOrders: ['traveller', 'itineraryOrders'] as const,
  // Individual entities - use these for specific queries
  itinerary: (userId: string) => ['traveller', 'itineraries', userId] as const,
  itineraries: (userId: number) => ['traveller', 'itineraries', userId] as const,
  itineraryDetail: (itineraryId: string | number) => ['traveller', 'itineraryDetail', itineraryId] as const,
  checkItineraries: (userId: number) => ['traveller', 'checkItineraries', userId] as const,
  favorites: (userId: number) => ['traveller', 'favorites', userId] as const,
  myOrders: (userId: number) => ['traveller', 'myOrders', userId] as const,
  myOrderDetails: (travellerId: number, orderId: number) => ['traveller', 'myOrderDetails', travellerId, orderId] as const,
  ratings: (travellerId: number) => ['traveller', 'ratings', travellerId] as const,
  cancellationPolicies: ['traveller', 'cancellationPolicies'] as const,
  shipmentDetails: (shipmentId: string) => ['traveller', 'shipmentDetails', shipmentId] as const,
  itineraryOrders: (itineraryId: string) => ['traveller', 'itineraryOrders', itineraryId] as const,
  orderDetails: (orderCode: string) => ['traveller', 'orderDetails', orderCode] as const,
  walletBalanceTransactions: (userId: string, startDate?: string, endDate?: string) => ['traveller', 'walletBalanceTransactions', userId, startDate, endDate] as const,
  walletMonthlyBalance: (userId: string) => ['traveller', 'walletMonthlyBalance', userId] as const,
  orderHistory: (userId: string, filter?: string) => ['traveller', 'orderHistory', userId, filter] as const,
  currencies: ['traveller', 'currencies'] as const,
  travelPreferences: (userId: string) => ['traveller', 'travelPreferences', userId] as const,
};

// API functions
const travellerApiClient = {
  // Base query function
  async request({ url, method = 'GET', body }: { url: string; method?: string; body?: unknown }) {
    try {
      const travellerService = getApiService(ServiceType.traveller2);
      const token = getAuth0Token();

      if (token) {
        travellerService.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      const response = await travellerService({
        url,
        method,
        data: body,
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: unknown;
        };
        message?: string;
      };
      throw {
        status: err.response?.status,
        data: err.response?.data || err.message,
      };
    }
  },

  // API endpoint implementations
  createItinerary: (body: {
    user_id: number;
    confirmation_number: string;
    flight_number: string;
    departure_airport_id: number;
    destination_airport_id: number;
    departure_date: string;
    departure_time: string;
    arrival_date: string;
    arrival_time: string;
    weight: number;
    price: number;
    drop_of_date: string;
    drop_of_time: string;
    pick_up_date: string;
    pick_up_time: string;
    sender_drop_off_address: string;
    receiver_drop_off_address: string;
  }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.CREATE_ITINERARY.path,
      method: 'POST',
      body,
    }),

  createItineraryCategory: (body: {
    user_id: string;
    name?: string;
    flight_number: string;
    departure_airport_id: number;
    destination_airport_id: number;
    departure_date: string;
    departure_time: string;
    arrival_date: string;
    arrival_time: string;
    weight: number;
    type: string;
    price: number;
    drop_of_date: string;
    drop_of_time: string;
    pick_up_date: string;
    pick_up_time: string;
    sender_drop_off_address: string;
    receiver_drop_off_address: string;
    itineraries: Array<{
      category_id: string;
    }>;
    time_zone: string;
  }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.CREATE_ITINERARY_CATEGORY.path,
      method: 'POST',
      body,
    }),

  updateItineraryCategory: (body: {
    itinerary_id: string;
    user_id: string;
    name?: string;
    flight_number: string;
    departure_airport_id: number;
    destination_airport_id: number;
    departure_date: string;
    departure_time: string;
    arrival_date: string;
    arrival_time: string;
    weight: number;
    type: string;
    price: number;
    drop_of_date: string;
    drop_of_time: string;
    pick_up_date: string;
    pick_up_time: string;
    sender_drop_off_address: string;
    receiver_drop_off_address: string;
    itineraries: Array<{
      category_id: string;
    }>;
    time_zone: string;
  }) => {
    const { itinerary_id, ...rest } = body;
    return travellerApiClient.request({
      url: traveller_ENDPOINTS.UPDATE_ITINERARY_CATEGORY.path.replace(':itinerary_id', String(itinerary_id)),
      method: 'PUT',
      body: rest,
    });
  },

  updateItinerary: (body: {
    itinerary_id: string;
    user_id: string;
    name?: string;
    confirmation_number: string;
    flight_number: string;
    departure_airport_id: number;
    destination_airport_id: number;
    departure_date: string;
    departure_time: string;
    arrival_date: string;
    arrival_time: string;
    weight: number;
    type: string;
    price: number;
    drop_of_date: string;
    drop_of_time: string;
    pick_up_date: string;
    pick_up_time: string;
    sender_drop_off_address: string;
    receiver_drop_off_address: string;
    ticket_image_id?: string;
    deleted_ticket_image_ids?: string[];
  }) => {
    const { itinerary_id, ...rest } = body;
    return travellerApiClient.request({
      url: traveller_ENDPOINTS.UPDATE_ITINERARY.path.replace(':itinerary_id', String(itinerary_id)),
      method: 'PUT',
      body: rest,
    });
  },

  uploadItineraryImages: ({
    user_id,
    itinerary_id,
    ticket,
    passport,
  }: {
    user_id: string;
    itinerary_id: string;
    ticket: File[];
    passport: File[];
  }) => {
    const formData = new FormData();
    formData.append('user_id', String(user_id));
    formData.append('itinerary_id', String(itinerary_id));
    ticket.forEach((file) => formData.append('ticket', file));
    passport.forEach((file) => formData.append('passport', file));

    return travellerApiClient.request({
      url: traveller_ENDPOINTS.UPLOAD_ITINERARY_IMAGE.path,
      method: 'POST',
      body: formData,
    });
  },

  searchSenders: (body: {
    traveller_id: number;
    fromLocation: number;
    toLocation?: number;
    startDate: string;
    endDate: string;
    page: number;
    limit: number;
    radius: number;
    latitude: number;
    longitude: number;
    sortBy?: string;
    min_weight?: number;
    max_weight?: number;
  }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.SEARCH_TRIPS.path,
      method: 'POST',
      body,
    }),

  searchPublicSender: (payload: {
    fromLocation: number;
    toLocation?: number;
    startDate: string;
    endDate: string;
    page: number;
    limit: number;
    radius: number;
    weight: number;
    latitude: number;
    longitude: number;
    sortBy: string;
  }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.PUBLIC_SENDER_SEARCH.path,
      method: 'POST',
      body: payload,
    }),

  // Traveler sends offer request to sender
  sendOfferAstraveller: (body: { shipment_id: string; itinerary_id: string; expiration_duration?: number }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.SEND_OFFER.path,
      method: 'POST',
      body,
    }),

  // Traveler accepts sender's offer request
  acceptOfferAstraveller: (body: { order_code: string }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.ACCEPT_OFFER.path,
      method: 'POST',
      body,
    }),

  // Traveler rejects sender's offer request
  rejectOfferAstraveller: (body: { order_code: string; reason?: string }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.OFFER_REJECT.path,
      method: 'POST',
      body,
    }),

  // Traveler withdraws their offer request
  withdrawOfferRequest: (body: { order_code: string }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.WITHDRAW_OFFER_REQUEST.path,
      method: 'POST',
      body,
    }),
  getMyTravelItineraryDetail: (itinerary_id: string) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.GET_ITINERARY_DETAILS.path.replace(
        ':itinerary_id',
        String(itinerary_id)
      ),
    }),
  deleteMyTravelsItineraryFortraveller: ({ traveller_id, itinerary_id }: { traveller_id: string; itinerary_id: number }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.DELETE_ITINERARY.path
        .replace(':traveller_id', String(traveller_id))
        .replace(':itinerary_id', String(itinerary_id)),
      method: 'DELETE',
    }),

  // Get itinerary details by itinerary_id
  getItineraryDetails: (itinerary_id: string) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.ITINERARY_DETAILS.path.replace(':itinerary_id', itinerary_id),
    }),

  // Update itinerary weight and price
  updateItineraryWeight: (itinerary_id: string, body: { weight: number; price: number }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.UPDATE_ITINERARY_WEIGHT.path.replace(':itinerary_id', itinerary_id),
      method: 'PUT',
      body,
    }),

  // Get orders for a specific itinerary
  getItineraryOrders: (itinerary_id: string) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.ITINERARY_ORDERS.path.replace(':itinerary_id', itinerary_id),
      method: 'GET',
    }),

  // Get order details by order code
  getOrderDetails: (order_code: string) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.ORDERS_DETAILS.path,
      method: 'POST',
      body: { order_code },
    }),
  approveEditItemRequest: (body: { item_id: string; order_code: string }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.APPROVE_EDIT_ITEM_REQUEST.path,
      method: 'POST',
      body,
    }),

  cancelEditItemRequest: (body: { item_id: string; order_code: string }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.CANCEL_EDIT_ITEM_REQUEST.path,
      method: 'POST',
      body,
    }),

  // Start pickup off
  startPickupOff: (body: {
    order_id: string;
    user_id: string;
    check_in_by: string;
    id_card_front_id?: string;
    id_card_back_id?: string;
    item_images: string[];
  }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.PICK_UP.path,
      method: 'POST',
      body,
    }),
  startDelivery: (body: {
    order_id: string;
    user_id: string;
    check_out_by: string;
    item_images: string[];
  }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.DELIVERY.path,
      method: 'POST',
      body,
    }),

  // Get wallet monthly balance
  getWalletMonthlyBalance: (userId: string) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.WALLET_MONTHLY_BALANCE.path.replace(':userId', userId),
      method: 'GET',
    }),

  // Get order history
  getOrderHistory: (userId: string, filter: 'all' | 'in_progress' | 'completed' | 'cancelled' = 'all') => {
    let url = traveller_ENDPOINTS.ORDER_HISTORY.path.replace(':userId', userId);
    const params = new URLSearchParams();
    if (filter && filter !== 'all') {
      params.append('filter', filter);
    }
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    return travellerApiClient.request({
      url,
      method: 'GET',
    });
  },

  // Get currencies
  getCurrencies: () =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.CURRENCY.path,
      method: 'GET',
    }),

  // Get travel preferences
  getTravelPreferences: (userId: string) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.TRAVEL_PREFERENCES.path.replace(':userId', userId),
      method: 'GET',
    }),

  // Update travel preferences
  updateTravelPreferences: (body: UpdateTravelPreferencesRequest) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.UPDATE_TRAVEL_PREFERENCES.path,
      method: 'POST',
      body,
    }),

  // Pin shipment to wishlist
  pinShipment: (params: { user_id: string; shipment_id: string }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.PIN_SHIPMENT.path,
      method: 'POST',
      body: params,
    }),

  // Unpin shipment from wishlist
  unpinShipment: (params: { user_id: string; shipment_id: string }) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.UNPIN_SHIPMENT.path,
      method: 'POST',
      body: params,
    }),

  // Search itineraries by name
  searchItineraries: (traveler_id: string, name?: string) => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    const queryParams = params.toString() ? `?${params.toString()}` : '';
    return travellerApiClient.request({
      url: traveller_ENDPOINTS.CHECK_ITINERARIES.path.replace(':id', traveler_id) + queryParams,
      method: 'GET',
    });
  },

  // Get pending requests for a specific itinerary
  getPendingRequestsByItinerary: (itinerary_id: string) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.GET_PENDING_REQUESTS_BY_ITINERARY.path.replace(':itinerary_id', itinerary_id),
      method: 'GET',
    }),

  // Get my pending requests for a specific itinerary (status = 'pending')
  getMyPendingRequestsByItinerary: (itinerary_id: string) =>
    travellerApiClient.request({
      url: traveller_ENDPOINTS.GET_MY_PENDING_REQUESTS_BY_ITINERARY.path.replace(':itinerary_id', itinerary_id),
      method: 'GET',
    }),

};

// TanStack Query hooks
export const useCreateItineraryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      user_id: number;
      confirmation_number: string;
      flight_number: string;
      departure_airport_id: number;
      destination_airport_id: number;
      departure_date: string;
      departure_time: string;
      arrival_date: string;
      arrival_time: string;
      weight: number;
      price: number;
      drop_of_date: string;
      drop_of_time: string;
      pick_up_date: string;
      pick_up_time: string;
      sender_drop_off_address: string;
      receiver_drop_off_address: string;
    }) => travellerApiClient.createItinerary(body),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: travellerKeys.itineraries(Number(variables.user_id)) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.checkItineraries(variables.user_id) });
    },
  });
};

export const useCreateItineraryCategoryMutation = () => {
  const queryClient = useQueryClient();
  const { timeZone } = useLocalization();

  return useMutation({
    mutationFn: (body: {
      user_id: string;
      name?: string;
      flight_number: string;
      departure_airport_id: number;
      destination_airport_id: number;
      departure_date: string;
      departure_time: string;
      arrival_date: string;
      arrival_time: string;
      weight: number;
      type: string;
      price: number;
      drop_of_date: string;
      drop_of_time: string;
      pick_up_date: string;
      pick_up_time: string;
      sender_drop_off_address: string;
      receiver_drop_off_address: string;
      itineraries: Array<{
        category_id: string;
      }>;
    }) => travellerApiClient.createItineraryCategory({ ...body, time_zone: timeZone }),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: travellerKeys.itineraries(Number(variables.user_id)) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.checkItineraries(Number(variables.user_id)) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraries });
    },
  });
};

export const useUpdateItineraryCategoryMutation = () => {
  const queryClient = useQueryClient();
  const { timeZone } = useLocalization();

  return useMutation({
    mutationFn: (body: {
      itinerary_id: string;
      user_id: string;
      name?: string;
      flight_number: string;
      departure_airport_id: number;
      destination_airport_id: number;
      departure_date: string;
      departure_time: string;
      arrival_date: string;
      arrival_time: string;
      weight: number;
      type: string;
      price: number;
      drop_of_date: string;
      drop_of_time: string;
      pick_up_date: string;
      pick_up_time: string;
      sender_drop_off_address: string;
      receiver_drop_off_address: string;
      itineraries: Array<{
        category_id: string;
      }>;
    }) => travellerApiClient.updateItineraryCategory({ ...body, time_zone: timeZone }),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: travellerKeys.itineraries(Number(variables.user_id)) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.checkItineraries(Number(variables.user_id)) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.itineraryDetail(variables.itinerary_id) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraries });
    },
  });
};

export const useUploadItineraryImagesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      user_id,
      itinerary_id,
      ticket,
      passport,
    }: {
      user_id: string;
      itinerary_id: string;
      ticket: File[];
      passport: File[];
    }) => travellerApiClient.uploadItineraryImages({
      user_id,
      itinerary_id,
      ticket,
      passport,
    }),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: travellerKeys.itineraries(Number(variables.user_id)) });
    },
  });
};
// Traveler sends offer request to sender
export const useSendOfferAstravellerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { shipment_id: string; itinerary_id: string; expiration_duration?: number }) =>
      travellerApiClient.sendOfferAstraveller(body),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraries });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
    },
  });
};

// Traveler accepts sender's offer request
export const useAcceptOfferAstravellerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { order_code: string }) =>
      travellerApiClient.acceptOfferAstraveller(body),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraries });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
    },
  });
};

// Traveler rejects sender's offer request
export const useRejectOfferAstravellerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { order_code: string; reason?: string }) =>
      travellerApiClient.rejectOfferAstraveller(body),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraries });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
    },
  });
};
// Traveler withdraws their offer request
export const useWithdrawOfferRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { order_code: string }) =>
      travellerApiClient.withdrawOfferRequest(body),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraries });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
    },
  });
};

export const useGetMyTravelItineraryDetailQuery = (itinerary_id: string, options = {}) =>
  useQuery({
    queryKey: travellerKeys.itineraryDetail(itinerary_id),
    queryFn: () => travellerApiClient.getMyTravelItineraryDetail(itinerary_id),
    ...options,
  });

// Get itinerary details query hook
export const useGetItineraryDetailsQuery = (itinerary_id: string, options = {}) =>
  useQuery({
    queryKey: travellerKeys.itineraryDetail(itinerary_id),
    queryFn: () => travellerApiClient.getItineraryDetails(itinerary_id),
    ...options,
  });

// Update itinerary weight mutation hook
export const useUpdateItineraryWeightMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itinerary_id, weight, price }: { itinerary_id: string; weight: number; price: number }) =>
      travellerApiClient.updateItineraryWeight(itinerary_id, { weight, price }),
    onSuccess: (_, variables) => {
      // Invalidate the specific itinerary detail
      queryClient.invalidateQueries({ queryKey: travellerKeys.itineraryDetail(variables.itinerary_id) });
      // Invalidate all itineraries to refresh the list
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraries });
    },
  });
};

// Get orders for a specific itinerary query hook
export const useGetItineraryOrdersQuery = (itinerary_id: string, options = {}) =>
  useQuery<{ itineraryOrders: ItineraryOrder[] }>({
    queryKey: travellerKeys.itineraryOrders(itinerary_id),
    queryFn: () => travellerApiClient.getItineraryOrders(itinerary_id),
    ...options,
  });

// Get order details mutation hook (using mutation since it's a POST request)
export const useGetOrderDetailsMutation = () => {
  return useMutation<OrderDetails, Error, { order_code: string }>({
    mutationFn: ({ order_code }: { order_code: string }) =>
      travellerApiClient.getOrderDetails(order_code),
  });
};
export const useSearchSendersMutation = () => {
  return useMutation({
    mutationFn: (body: {
      traveller_id: number;
      fromLocation: number;
      toLocation?: number;
      startDate: string;
      endDate: string;
      page: number;
      limit: number;
      radius: number;
      latitude: number;
      longitude: number;
      sortBy?: string;
      min_weight?: number;
      max_weight?: number;
    }) => travellerApiClient.searchSenders(body),
  });
};

export const useSearchPublicSenderMutation = () => {
  return useMutation({
    mutationFn: ({ payload }: {
      payload: {
        fromLocation: number;
        toLocation?: number;
        startDate: string;
        endDate: string;
        page: number;
        limit: number;
        radius: number;
        weight: number;
        min_weight?: number;
        max_weight?: number;
        latitude: number;
        longitude: number;
        sortBy: string;
      }
    }) => travellerApiClient.searchPublicSender(payload),
  });
};
export const useApproveEditItemRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { item_id: string; order_code: string }) =>
      travellerApiClient.approveEditItemRequest(body),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrderDetails });
    },
  });
};

export const useCancelEditItemRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { item_id: string; order_code: string }) =>
      travellerApiClient.cancelEditItemRequest(body),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrderDetails });
    },
  });
};

export const useStartPickupOffMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      order_id: string;
      user_id: string;
      check_in_by: string;
      id_card_front_id?: string;
      id_card_back_id?: string;
      item_images: string[];
    }) => travellerApiClient.startPickupOff(body),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrderDetails });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraryOrders });
    },
  });
};

export const useStartDeliveryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      order_id: string;
      user_id: string;
      check_out_by: string;
      item_images: string[];
    }) => travellerApiClient.startDelivery(body),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrderDetails });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraryOrders });
    },
  });
};

type UpdateItineraryBody = {
  itinerary_id: string;
  user_id: string;
  name?: string;
  confirmation_number: string;
  flight_number: string;
  departure_airport_id: number;
  destination_airport_id: number;
  departure_date: string;
  departure_time: string;
  arrival_date: string;
  arrival_time: string;
  weight: number;
  type: string;
  price: number;
  drop_of_date: string;
  drop_of_time: string;
  pick_up_date: string;
  pick_up_time: string;
  sender_drop_off_address: string;
  receiver_drop_off_address: string;
  ticket_image_id?: string;
  deleted_ticket_image_ids?: string[];
};

export const useUpdateItineraryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateItineraryBody) => travellerApiClient.updateItinerary(body),
    onMutate: async (variables) => {
      const { itinerary_id, user_id } = variables;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: travellerKeys.itineraries(Number(user_id)) });
      await queryClient.cancelQueries({ queryKey: travellerKeys.itineraryDetail(itinerary_id) });

      // Snapshot previous values
      const previousItineraries = queryClient.getQueryData(travellerKeys.itineraries(Number(user_id)));
      const previousItineraryDetail = queryClient.getQueryData(travellerKeys.itineraryDetail(itinerary_id));

      // Optimistically update itineraries list
      queryClient.setQueryData(travellerKeys.itineraries(Number(user_id)), (old: unknown) => {
        const oldData = old as { data?: unknown[] } | undefined;
        if (!oldData?.data) return old;
        return {
          ...oldData,
          data: oldData.data.map((itinerary: unknown) => {
            const it = itinerary as Record<string, unknown>;
            return it.itinerary_id === itinerary_id ? { ...it, ...variables } : itinerary;
          }),
        };
      });

      // Optimistically update itinerary details
      queryClient.setQueryData(travellerKeys.itineraryDetail(itinerary_id), (old: unknown) => {
        if (!old) return old;
        return { ...(old as Record<string, unknown>), ...variables };
      });

      return { previousItineraries, previousItineraryDetail };
    },
    onError: (_error: unknown, variables: UpdateItineraryBody, context: { previousItineraries?: unknown; previousItineraryDetail?: unknown } | undefined) => {
      const { itinerary_id, user_id } = variables;

      // Rollback on error
      if (context?.previousItineraries) {
        queryClient.setQueryData(travellerKeys.itineraries(Number(user_id)), context.previousItineraries);
      }
      if (context?.previousItineraryDetail) {
        queryClient.setQueryData(travellerKeys.itineraryDetail(itinerary_id), context.previousItineraryDetail);
      }
    },
    onSettled: (_, __, variables) => {
      // Refetch to ensure data consistency
      queryClient.invalidateQueries({ queryKey: travellerKeys.itineraries(Number(variables.user_id)) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.checkItineraries(Number(variables.user_id)) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.myOrders(Number(variables.user_id)) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.itineraryDetail(variables.itinerary_id) });
    },
  });
};
export const useDeleteMyTravelsItineraryFortravellerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ traveller_id, itinerary_id }: { traveller_id: string; itinerary_id: number }) =>
      travellerApiClient.deleteMyTravelsItineraryFortraveller({ traveller_id, itinerary_id }),
    onMutate: async (variables: { traveller_id: string; itinerary_id: number }) => {
      const { traveller_id, itinerary_id } = variables;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: travellerKeys.itineraries(Number(traveller_id)) });
      await queryClient.cancelQueries({ queryKey: travellerKeys.itineraryDetail(String(itinerary_id)) });

      // Snapshot previous values
      const previousItineraries = queryClient.getQueryData(travellerKeys.itineraries(Number(traveller_id)));
      const previousItineraryDetail = queryClient.getQueryData(travellerKeys.itineraryDetail(String(itinerary_id)));

      // Optimistically remove itinerary from list
      queryClient.setQueryData(travellerKeys.itineraries(Number(traveller_id)), (old: unknown) => {
        const oldData = old as { data?: unknown[] } | undefined;
        if (!oldData?.data) return old;
        return {
          ...oldData,
          data: oldData.data.filter((itinerary: unknown) => (itinerary as Record<string, unknown>).itinerary_id !== itinerary_id),
        };
      });

      // Remove itinerary details from cache
      queryClient.removeQueries({ queryKey: travellerKeys.itineraryDetail(String(itinerary_id)) });

      return { previousItineraries, previousItineraryDetail };
    },
    onError: (_error: unknown, variables: { traveller_id: string; itinerary_id: number }, context: { previousItineraries?: unknown; previousItineraryDetail?: unknown } | undefined) => {
      const { traveller_id, itinerary_id } = variables;

      // Rollback on error
      if (context?.previousItineraries) {
        queryClient.setQueryData(travellerKeys.itineraries(Number(traveller_id)), context.previousItineraries);
      }
      if (context?.previousItineraryDetail) {
        queryClient.setQueryData(travellerKeys.itineraryDetail(String(itinerary_id)), context.previousItineraryDetail);
      }
    },
    onSettled: (_, __, variables) => {
      // Refetch to ensure data consistency
      queryClient.invalidateQueries({ queryKey: travellerKeys.itinerary(variables.traveller_id) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.itineraries(Number(variables.traveller_id)) });
    },
  });
};
// Get wallet monthly balance query hook
export const useGetWalletMonthlyBalanceQuery = (userId: string, options = {}) =>
  useQuery<WalletMonthlyBalanceResponse>({
    queryKey: travellerKeys.walletMonthlyBalance(userId),
    queryFn: () => travellerApiClient.getWalletMonthlyBalance(userId),
    enabled: !!userId,
    refetchOnMount: 'always',
    ...options,
  });

// Get order history query hook
export const useGetOrderHistoryQuery = (
  userId: string,
  filter: 'all' | 'in_progress' | 'completed' | 'cancelled' = 'all',
  options = {}
) =>
  useQuery<OrderHistoryResponse>({
    queryKey: travellerKeys.orderHistory(userId, filter),
    queryFn: () => travellerApiClient.getOrderHistory(userId, filter),
    enabled: !!userId,
    ...options,
  });

// Get currencies query hook
export const useGetCurrenciesQuery = (options = {}) =>
  useQuery<CurrencyResponse>({
    queryKey: travellerKeys.currencies,
    queryFn: () => travellerApiClient.getCurrencies(),
    ...options,
  });

// Get travel preferences query hook
export const useGetTravelPreferencesQuery = (userId: string, options: { enabled?: boolean; [key: string]: unknown } = {}) => {
  const { enabled, ...rest } = options;
  return useQuery<TravelPreferencesResponse>({
    queryKey: travellerKeys.travelPreferences(userId),
    queryFn: () => travellerApiClient.getTravelPreferences(userId),
    enabled: !!userId && enabled !== false,
    ...rest,
  });
};

// Update travel preferences mutation hook
export const useUpdateTravelPreferencesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, UpdateTravelPreferencesRequest>({
    mutationFn: (body: UpdateTravelPreferencesRequest) =>
      travellerApiClient.updateTravelPreferences(body),
    onSuccess: (_, variables) => {
      // Invalidate the specific user's travel preferences
      queryClient.invalidateQueries({ queryKey: travellerKeys.travelPreferences(variables.user_id) });
    },
  });
};

// Pin shipment to wishlist mutation
export const usePinShipmentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { user_id: string; shipment_id: string }) =>
      travellerApiClient.pinShipment(params),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['traveller', 'favorites'] });
    },
  });
};

// Unpin shipment from wishlist mutation
export const useUnpinShipmentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { user_id: string; shipment_id: string }) =>
      travellerApiClient.unpinShipment(params),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['traveller', 'favorites'] });
    },
  });
};

// Search itineraries by name query
export const useSearchItinerariesQuery = (traveler_id: string, name?: string, options = {}) =>
  useQuery({
    queryKey: ['traveller', 'searchItineraries', traveler_id, name] as const,
    queryFn: () => travellerApiClient.searchItineraries(traveler_id, name),
    enabled: !!traveler_id,
    ...options,
  });

// Get pending requests by itinerary ID query
export const useGetPendingRequestsByItineraryQuery = (itinerary_id: string, options = {}) =>
  useQuery({
    queryKey: ['traveller', 'pendingRequests', itinerary_id] as const,
    queryFn: () => travellerApiClient.getPendingRequestsByItinerary(itinerary_id),
    enabled: !!itinerary_id,
    ...options,
  });

// Get my pending requests by itinerary ID query (status = 'pending')
export const useGetMyPendingRequestsByItineraryQuery = (itinerary_id: string, options = {}) =>
  useQuery({
    queryKey: ['traveller', 'myPendingRequests', itinerary_id] as const,
    queryFn: () => travellerApiClient.getMyPendingRequestsByItinerary(itinerary_id),
    enabled: !!itinerary_id,
    ...options,
  });